import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useMedia } from "@/context/MediaContext";
import { MediaFrame } from "@/components/media/MediaFrame";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] } }),
};

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

// Four vertical pieces of real VVM content, staggered like contact sheets on a desk.
const HeroMedia = ({ items, progress, reduced }) => {
  const speeds = [-140, -60, -200, -100];
  const offsets = ["mt-24", "mt-0", "mt-40", "mt-12"];
  const widths = ["w-[15vw] max-w-[220px]", "w-[13vw] max-w-[190px]", "w-[16vw] max-w-[240px]", "w-[12vw] max-w-[180px]"];
  return (
    <div className="hidden lg:flex absolute right-[3vw] top-[14vh] gap-5 items-start pointer-events-auto" aria-hidden="false">
      {items.slice(0, 4).map((item, i) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const y = useTransform(progress, [0, 1], [0, reduced ? 0 : speeds[i]]);
        return (
          <motion.div key={item.id} style={{ y }} className={`${offsets[i]} ${widths[i]} shrink-0`}
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.9 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
            <MediaFrame item={item} autoplay hoverPlay={false} priority={i < 2} className="rounded-sm shadow-[0_40px_80px_-30px_rgba(46,27,22,0.35)]" />
          </motion.div>
        );
      })}
    </div>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -80]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0.15]);
  const { items, hasReal } = useMedia("hero", 0);

  return (
    <section ref={ref} className={`relative pt-32 md:pt-40 overflow-hidden ${hasReal ? "min-h-[100svh]" : "min-h-[88svh] pb-24 flex flex-col justify-center"}`} data-testid="hero" aria-labelledby="hero-heading">
      {hasReal && <HeroMedia items={items} progress={scrollYProgress} reduced={reduced} />}

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-20 max-w-[52rem]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }} className="label-eyebrow text-[#38BDF8] mb-8">
            Strategy · Content · Paid · Websites · Systems
          </motion.p>

          <h1 id="hero-heading" className="font-display leading-[0.9] text-[#2E1B16] text-[2.9rem] sm:text-6xl md:text-7xl lg:text-[5.4rem] uppercase">
            {["Marketing built", "around", "your business,"].map((line, i) => (
              <span key={i} className="reveal-mask">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="show" className="block">{line}</motion.span>
              </span>
            ))}
            <span className="reveal-mask">
              <motion.span custom={3} variants={lineVariants} initial="hidden" animate="show" className="block">
                <span className="font-accent font-normal normal-case text-[#38BDF8] text-[1.15em] tracking-normal">not the algorithm.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9 }} className="mt-10 text-lg md:text-xl text-[#2E1B16]/65 max-w-xl leading-relaxed">
            We help food businesses and growing Australian brands turn what happens behind the scenes into content people actually want to watch, then back it with strategy, paid advertising and systems built around the business.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <button onClick={() => scrollTo("contact")} data-testid="hero-cta" className="group inline-flex items-center gap-2 rounded-full bg-[#2E1B16] text-[#FAF7F2] font-semibold px-7 py-4 hover:bg-[#38BDF8] hover:text-[#2E1B16] transition-colors duration-300">
              Tell us about your business
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <Link to="/work" data-testid="hero-secondary" className="inline-flex items-center gap-2 px-2 py-4 font-semibold text-[#2E1B16] link-underline">
              See our work
            </Link>
          </motion.div>
        </motion.div>

        {/* Mobile / tablet: native feeling horizontal strip */}
        {hasReal && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
            className="lg:hidden mt-16 -mx-6 px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-4" data-testid="hero-media-mobile">
            {items.slice(0, 4).map((item, i) => (
              <div key={item.id} className={`snap-start shrink-0 w-[46vw] sm:w-[30vw] ${i % 2 ? "mt-8" : ""}`}>
                <MediaFrame item={item} autoplay hoverPlay={false} priority={i < 2} className="rounded-sm" />
              </div>
            ))}
          </motion.div>
        )}

        {hasReal && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }} className="hidden lg:block absolute right-[3vw] bottom-10 font-annotation text-2xl text-[#2E1B16]/55 -rotate-2">
            real businesses, real footage ↗
          </motion.p>
        )}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#FAF7F2]" />
    </section>
  );
};

export default Hero;
